/**
 * Playing together on one device (1.7.0): the arithmetic behind `Standings`
 * and `PlayerNames`, pure and game-agnostic, so a game ranks its players and
 * checks their names the way the parts draw them.
 *
 * Nothing here knows what a score is or how a game ends: a value is a number
 * to compare, a name is a string to tell apart.
 */
import { shellText } from './i18n.svelte.js';

/**
 * Standard competition ranks ("1224") for `values`, in their own order:
 * equal values share a rank, and the rank after a tie skips the places the
 * tie took. `higher` (the default) ranks the largest value first (a score);
 * `lower` ranks the smallest first (a time).
 */
export function competitionRanks(
  values: readonly number[],
  better: 'higher' | 'lower' = 'higher',
): number[] {
  const beats = (a: number, b: number): boolean => (better === 'higher' ? a > b : a < b);
  return values.map((value) => 1 + values.filter((other) => beats(other, value)).length);
}

/** Whether `rank` is shared by more than one of `ranks`. */
export function isTiedRank(ranks: readonly number[], rank: number): boolean {
  return ranks.filter((each) => each === rank).length > 1;
}

/** The shell's default name for the player in seat `index` (0-based): "Player 1", "Player 2"… */
export function defaultPlayerName(index: number): string {
  return shellText('playerNamesDefault', { n: index + 1 });
}

/**
 * Control and format characters (a bell, a zero-width space, a direction
 * mark): invisible, so two names that look alike would not compare alike,
 * and a game's own reader may refuse them.
 */
const INVISIBLE = /[\p{Cc}\p{Cf}]/gu;

/**
 * The names to play with: control and format characters dropped, each
 * trimmed (inner runs of white space folded to one space), and a blank one
 * replaced by its seat's default. What a game stores and shows once the
 * setup is done.
 */
export function resolvePlayerNames(
  names: readonly string[],
  defaultName: (index: number) => string = defaultPlayerName,
): string[] {
  return names.map((name, index) => {
    const folded = name.replace(/\s+/g, ' ').replace(INVISIBLE, '').trim();
    return folded.length > 0 ? folded : defaultName(index);
  });
}

/** One name as it is compared: NFKC, invisible characters dropped, lower case, white space folded. */
function nameKey(name: string): string {
  return name
    .normalize('NFKC')
    .replace(INVISIBLE, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase();
}

/** What is wrong with one seat's name, if anything. */
export type PlayerNameProblem = 'duplicate' | null;

/**
 * Per seat, what is wrong with its name once resolved: `duplicate` when an
 * earlier seat already has it (compared case-insensitively, NFKC, so "Noor"
 * and "noor" are one name, and a typed "Player 2" in seat 1 clashes with
 * seat 2's default). The first holder of a name is never the problem.
 */
export function playerNameProblems(
  names: readonly string[],
  defaultName: (index: number) => string = defaultPlayerName,
): PlayerNameProblem[] {
  const seen = new Set<string>();
  return resolvePlayerNames(names, defaultName).map((name) => {
    const key = nameKey(name);
    if (seen.has(key)) return 'duplicate';
    seen.add(key);
    return null;
  });
}

/** Whether every seat's name is usable (no problems). */
export function playerNamesValid(
  names: readonly string[],
  defaultName: (index: number) => string = defaultPlayerName,
): boolean {
  return playerNameProblems(names, defaultName).every((problem) => problem === null);
}
